import React, { useState } from 'react';
import {
  Drawer, Box, Typography, IconButton, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Button, Divider, MenuItem, Select, FormControl,
  InputLabel, InputAdornment, Grid, Chip, Tooltip, CircularProgress, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import EmailIcon from '@mui/icons-material/Email';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { lineNet, money, VAT_PRESETS } from '../utils/quoteMath';
import { downloadQuotePdf, quotePdfBase64, quoteFilename } from '../utils/quotePdf';
import ShippingPicker from './ShippingPicker';
import QuotePdfPreview from './QuotePdfPreview';
import useLiveQuotePdf from '../hooks/useLiveQuotePdf';

const EMAIL_API = 'https://elnaf.fi/apiElnaf/api/b2b_quote.php';

const QuoteDrawer = ({ open, onClose, quote, totals, updateLine, removeLine, setQty, setField, setCustomerField, setShipping, clearLines, resetQuote }) => {
  const [busy, setBusy] = useState('');            // '' | 'pdf' | 'email'
  const [toast, setToast] = useState(null);        // { severity, msg }
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailCc, setEmailCc] = useState('sales@elnaf.fi');
  const [emailMsg, setEmailMsg] = useState('');
  const [showPreview, setShowPreview] = useState(true);

  const hasLines = quote.lines.length > 0;

  // Only render while the drawer is open and the pane is visible — a hidden
  // iframe would still pay for a full jsPDF pass on every keystroke.
  const preview = useLiveQuotePdf(quote, open && showPreview);

  const handleDownload = async () => {
    setBusy('pdf');
    try {
      await downloadQuotePdf(quote);
      setToast({ severity: 'success', msg: `Downloaded ${quoteFilename(quote)}` });
    } catch (e) {
      setToast({ severity: 'error', msg: 'PDF generation failed: ' + e.message });
    } finally { setBusy(''); }
  };

  const openEmail = () => {
    setEmailTo(quote.customer?.email || '');
    setEmailMsg('');
    setEmailOpen(true);
  };

  const handleSendEmail = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTo)) {
      setToast({ severity: 'error', msg: 'Enter a valid recipient email.' });
      return;
    }
    setBusy('email');
    try {
      const pdfBase64 = await quotePdfBase64(quote);
      const res = await fetch(EMAIL_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: emailTo,
          cc: emailCc || undefined,
          quoteNo: quote.quoteNo,
          filename: quoteFilename(quote),
          message: emailMsg || undefined,
          pdfBase64,
        }),
      });
      const data = await res.json();
      if (res.ok && data.result === 1) {
        setEmailOpen(false);
        setToast({ severity: 'success', msg: `Quote emailed to ${emailTo}` });
      } else {
        setToast({ severity: 'error', msg: data.error || 'Failed to send email' });
      }
    } catch (e) {
      setToast({ severity: 'error', msg: 'Network error while sending.' });
    } finally { setBusy(''); }
  };

  const cust = quote.customer || {};
  const custField = (key, label, extra = {}) => (
    <TextField
      size="small" fullWidth label={label}
      value={cust[key] || ''}
      onChange={(e) => setCustomerField(key, e.target.value)}
      {...extra}
    />
  );

  return (
    <>
      <Drawer
        anchor="bottom" open={open} onClose={onClose}
        PaperProps={{ sx: { height: '92vh', borderTopLeftRadius: 16, borderTopRightRadius: 16, overflow: 'hidden' } }}
      >
        {/* Header */}
        <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
          <ReceiptLongIcon sx={{ color: '#845adf' }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>Quotation Builder</Typography>
            <Typography variant="caption" sx={{ color: '#6b7280' }}>
              {quote.quoteNo || 'No quote number yet'} · {quote.lines.length} item{quote.lines.length === 1 ? '' : 's'} · {totals.totalQty} pcs
              {totals.totalWeight > 0 && ` · ${totals.totalWeight} kg`}
            </Typography>
          </Box>

          <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button size="small" onClick={() => setShowPreview((v) => !v)}
                    startIcon={showPreview ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    sx={{ textTransform: 'none', color: '#6b7280' }}>
              {showPreview ? 'Hide preview' : 'Show preview'}
            </Button>
            <Button size="small" variant="outlined" startIcon={busy === 'pdf' ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
                    onClick={handleDownload} disabled={!hasLines || !!busy}
                    sx={{ textTransform: 'none', borderColor: '#845adf', color: '#845adf', '&:hover': { borderColor: '#6c48c5', backgroundColor: '#f3f0ff' } }}>
              Export PDF
            </Button>
            <Button size="small" variant="contained" startIcon={<EmailIcon />} onClick={openEmail} disabled={!hasLines || !!busy}
                    sx={{ textTransform: 'none', backgroundColor: '#845adf', '&:hover': { backgroundColor: '#6c48c5' } }}>
              Send by Email
            </Button>
            <IconButton onClick={onClose}><CloseIcon /></IconButton>
          </Box>
        </Box>

        {/* Body: [line items over live PDF] · form (right) */}
        <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
          {/* LEFT COLUMN — line items stacked above the live PDF */}
          <Box sx={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{
            flex: showPreview ? '0 1 auto' : '1 1 auto',
            maxHeight: showPreview ? '46%' : 'none',
            minHeight: showPreview ? 180 : 0,
            overflowY: 'auto', p: 3, backgroundColor: '#fafbfc',
          }}>
            {!hasLines ? (
              <Box sx={{ textAlign: 'center', py: 10, border: '1px dashed #d1d5db', borderRadius: 2, backgroundColor: 'white' }}>
                <ReceiptLongIcon sx={{ fontSize: 44, color: '#d1d5db', mb: 1 }} />
                <Typography variant="h6" sx={{ color: '#6b7280', mb: 0.5 }}>No items yet</Typography>
                <Typography variant="caption" sx={{ color: '#9ca3af' }}>Search the catalog and hit “Add” to start a quotation.</Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#374151' }}>Line items</Typography>
                  <Button size="small" startIcon={<DeleteSweepIcon />} onClick={clearLines}
                          sx={{ ml: 'auto', textTransform: 'none', color: '#dc2626' }}>
                    Clear all
                  </Button>
                </Box>

                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: 10.5, color: '#6b7280' }}>SKU / PRODUCT</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 10.5, color: '#6b7280', width: 118 }} align="center">QTY</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 10.5, color: '#6b7280', width: 118 }} align="right">UNIT €</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 10.5, color: '#6b7280', width: 96 }} align="center">DISC %</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 10.5, color: '#6b7280', width: 100 }} align="right">LINE TOTAL</TableCell>
                        <TableCell sx={{ width: 44 }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {quote.lines.map((l) => {
                        const priceChanged = Number(l.price) !== Number(l.listPrice);
                        return (
                          <TableRow key={l.sku} hover>
                            <TableCell sx={{ maxWidth: 300 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 12 }}>{l.sku}</Typography>
                              <Tooltip title={l.description || l.name}>
                                <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {l.description || l.name}
                                </Typography>
                              </Tooltip>
                              {l.stock <= 0 && <Chip label="Out of stock" size="small" sx={{ height: 17, fontSize: 9, mt: 0.4, backgroundColor: '#fee2e2', color: '#991b1b' }} />}
                            </TableCell>

                            <TableCell align="center">
                              <TextField size="small" type="number" value={l.qty}
                                         onChange={(e) => setQty(l.sku, e.target.value)}
                                         inputProps={{ min: 1, style: { textAlign: 'center', padding: '6px 4px', fontWeight: 700 } }} />
                            </TableCell>

                            <TableCell align="right">
                              <TextField size="small" type="number" value={l.price}
                                         onChange={(e) => updateLine(l.sku, { price: Math.max(0, Number(e.target.value) || 0) })}
                                         inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right', padding: '6px 4px' } }}
                                         InputProps={{ startAdornment: <InputAdornment position="start" sx={{ mr: 0 }}><Typography variant="caption">€</Typography></InputAdornment> }} />
                              {priceChanged && (
                                <Tooltip title={`Catalog price: ${money(l.listPrice)}`}>
                                  <Typography variant="caption" sx={{ display: 'block', color: '#d97706', fontSize: 9.5, mt: 0.2 }}>
                                    was {money(l.listPrice)}
                                  </Typography>
                                </Tooltip>
                              )}
                            </TableCell>

                            <TableCell align="center">
                              <TextField size="small" type="number" value={l.discountPct}
                                         onChange={(e) => updateLine(l.sku, { discountPct: Math.min(100, Math.max(0, Number(e.target.value) || 0)) })}
                                         inputProps={{ min: 0, max: 100, step: 0.5, style: { textAlign: 'center', padding: '6px 4px' } }} />
                            </TableCell>

                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>{money(lineNet(l))}</Typography>
                            </TableCell>

                            <TableCell align="center">
                              <IconButton size="small" color="error" onClick={() => removeLine(l.sku)}><DeleteIcon fontSize="small" /></IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TextField
                  fullWidth multiline rows={2} size="small" label="Notes (printed on the PDF)"
                  value={quote.notes} onChange={(e) => setField('notes', e.target.value)}
                  sx={{ mt: 2, backgroundColor: 'white' }}
                />
              </>
            )}
          </Box>

            {/* BELOW THE LIST — live PDF, rerendered (debounced) on every edit */}
            {showPreview && (
              <Box sx={{ flex: '1 1 auto', minHeight: 0, borderTop: '1px solid #e5e7eb' }}>
                <QuotePdfPreview
                  url={preview.url}
                  building={preview.building}
                  error={preview.error}
                  hasLines={hasLines}
                />
              </Box>
            )}
          </Box>

          {/* RIGHT — customer, shipping, VAT, totals */}
          <Box sx={{ width: 400, flexShrink: 0, borderLeft: '1px solid #e5e7eb', overflowY: 'auto', p: 2.5, backgroundColor: 'white' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#374151', mb: 1.5 }}>Quote details</Typography>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <TextField size="small" fullWidth label="Quote no." value={quote.quoteNo}
                           onChange={(e) => setField('quoteNo', e.target.value)} />
              </Grid>
              <Grid item xs={6}>
                <TextField size="small" fullWidth type="date" label="Date" InputLabelProps={{ shrink: true }}
                           value={quote.date} onChange={(e) => setField('date', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField size="small" fullWidth type="date" label="Valid until" InputLabelProps={{ shrink: true }}
                           value={quote.validUntil} onChange={(e) => setField('validUntil', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField size="small" fullWidth label="Subject (headline on PDF)" placeholder="Auto-generated if left empty"
                           value={quote.subject || ''} onChange={(e) => setField('subject', e.target.value)} />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#374151', mb: 1.5 }}>Customer</Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12}>{custField('company', 'Company')}</Grid>
              <Grid item xs={6}>{custField('contact', 'Contact person')}</Grid>
              <Grid item xs={6}>{custField('vatId', 'VAT ID')}</Grid>
              <Grid item xs={7}>{custField('email', 'Email', { type: 'email' })}</Grid>
              <Grid item xs={5}>{custField('phone', 'Phone')}</Grid>
              <Grid item xs={12}>{custField('address', 'Address')}</Grid>
              <Grid item xs={4}>{custField('postcode', 'Postcode')}</Grid>
              <Grid item xs={8}>{custField('city', 'City')}</Grid>
              <Grid item xs={12}>{custField('country', 'Country')}</Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#374151', mb: 1.5 }}>Shipping</Typography>
            <ShippingPicker
              shipping={quote.shipping}
              setShipping={setShipping}
              totalWeight={totals.totalWeight}
              customerCountry={quote.customer?.country}
            />

            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#374151', mt: 2.5, mb: 1.5 }}>VAT</Typography>
            <FormControl size="small" fullWidth>
              <InputLabel>VAT rate</InputLabel>
              <Select label="VAT rate" value={quote.vatRate} onChange={(e) => setField('vatRate', Number(e.target.value))}>
                {VAT_PRESETS.map((v) => <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>)}
              </Select>
            </FormControl>

            <Alert severity="info" sx={{ mt: 1.5, py: 0.4, fontSize: 11.5 }}>
              VAT is calculated on <strong>products + shipping</strong>.
            </Alert>

            <Divider sx={{ my: 2 }} />

            {/* Totals */}
            <Box sx={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 2, p: 2 }}>
              {[
                ['Subtotal', money(totals.subtotal + totals.discountTotal), {}],
                ...(totals.discountTotal > 0 ? [['Discount', '-' + money(totals.discountTotal), { color: '#b45309' }]] : []),
                [`Shipping${quote.shipping?.carrier ? ` (${quote.shipping.carrier})` : ''}`, money(totals.shipping), {}],
              ].map(([l, v, s], i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
                  <Typography variant="body2" sx={{ color: s.color || '#6b7280' }}>{l}</Typography>
                  <Typography variant="body2" sx={{ color: s.color || '#374151', fontWeight: 500 }}>{v}</Typography>
                </Box>
              ))}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8, pt: 0.8, borderTop: '1px dashed #d1d5db' }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#111827' }}>Net total</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#111827' }}>{money(totals.netTotal)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>VAT ({totals.vatRate}%)</Typography>
                <Typography variant="body2" sx={{ color: '#374151', fontWeight: 500 }}>{money(totals.vatAmount)}</Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', pt: 1.2, borderTop: '2px solid #845adf' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#111827' }}>TOTAL</Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#845adf' }}>{money(totals.total)}</Typography>
              </Box>

              {totals.vatRate === 0 && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#6b7280', fontStyle: 'italic', fontSize: 10 }}>
                  VAT 0% — reverse charge (Art. 44 EU VAT Directive).
                </Typography>
              )}
            </Box>

            <Button fullWidth size="small" onClick={resetQuote} sx={{ mt: 2, textTransform: 'none', color: '#dc2626' }}>
              Reset entire quote
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Email dialog */}
      <Dialog open={emailOpen} onClose={() => !busy && setEmailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Send quotation by email</DialogTitle>
        <DialogContent>
          <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 2 }}>
            The PDF ({quoteFilename(quote)}) will be attached automatically.
          </Typography>
          <TextField fullWidth size="small" label="To" type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} sx={{ mb: 2 }} autoFocus />
          <TextField fullWidth size="small" label="CC (optional)" type="email" value={emailCc} onChange={(e) => setEmailCc(e.target.value)} sx={{ mb: 2 }} />
          <TextField fullWidth size="small" label="Message (optional — a default is used if empty)" multiline rows={4}
                     value={emailMsg} onChange={(e) => setEmailMsg(e.target.value)} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEmailOpen(false)} disabled={!!busy}>Cancel</Button>
          <Button variant="contained" onClick={handleSendEmail} disabled={busy === 'email'}
                  startIcon={busy === 'email' ? <CircularProgress size={18} color="inherit" /> : <EmailIcon />}
                  sx={{ backgroundColor: '#845adf', '&:hover': { backgroundColor: '#6c48c5' } }}>
            {busy === 'email' ? 'Sending…' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast && <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)}>{toast.msg}</Alert>}
      </Snackbar>
    </>
  );
};

export default QuoteDrawer;
